import useAccessStore from '@/store/accessStore'
import { logError } from '@/utils/sentry'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const allowedPathsNoAuth = new Set<string>([
  '/login',
  '/forgot-password',
  '/forgot-password/enter-otp',
  '/forgot-password/create-new-password',
  '/registration',
  '/registration/enter-otp',
])

const validRegistrationPaths = [
  '/registration',
  '/registration/enter-otp',
  '/registration/profile',
  '/registration/industries',
  '/registration/business',
  '/registration/upload-logo',
  '/registration/account-ready',
]

// interface BrandData {
//   registrationStep?: number
//   firstName?: string
//   lastName?: string
//   phoneNumber?: string
//   region?: string
//   industries?: string[]
//   companyName?: string
//   jobPosition?: string
//   annualBudgetCurrency?: string
//   annualBudget?: string
//   companySize?: string
//   brandLogoUrl?: string | unknown
//   [key: string]: unknown
// }

// OLD LOGIC - Commented out in favor of registrationStep from backend
// const getCurrentRegistrationStep = (brandData: BrandData | null): string | null => {
//   if (!brandData) return null

//   if (!brandData.firstName || !brandData.lastName || !brandData.phoneNumber || !brandData.region) {
//     return '/registration/profile'
//   }
//   if (!brandData.industries || !Array.isArray(brandData.industries) || brandData.industries.length === 0) {
//     return '/registration/industries'
//   }
//   if (
//     !brandData.companyName ||
//     !brandData.jobPosition ||
//     !brandData.annualBudgetCurrency ||
//     !brandData.annualBudget ||
//     !brandData.companySize
//   ) {
//     return '/registration/business'
//   }
//   const brandLogoUrl = brandData.brandLogoUrl as string | undefined
//   if (!brandLogoUrl) {
//     return '/registration/upload-logo'
//   }
//   return '/registration/account-ready'
// }

// Get the previous registration step route (used by Back buttons)
// export const getPreviousRegistrationStep = (currentStep: number | undefined): string | null => {
//   switch (currentStep) {
//     case 2:
//       return null // Should log out
//     case 3:
//       return '/registration/profile'
//     case 4:
//       return '/registration/industries'
//     case 5:
//       return '/registration/business'
//     case 6:
//       return '/registration/upload-logo'
//     default:
//       return null
//   }
// }

export const useAuthGuard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, initialized, registrationStatus, brandData, checkRegistrationStatus } = useAccessStore()
  const hasFetchedRef = useRef(false)

  useEffect(() => {
    if (!initialized) return

    const decideRoute = async () => {
      const path = location.pathname
      const bd = brandData

      if (!isAuthenticated) {
        if (!allowedPathsNoAuth.has(path)) {
          navigate('/login', { replace: true })
        }
        return
      }

      if (!registrationStatus) {
        try {
          await checkRegistrationStatus()
        } catch (_e) {
          /* ignore */
        }
      }

      // Fetch brand data if not available (but skip for OTP pages and non-verified users)
      const shouldFetchBrandData = !bd && !hasFetchedRef.current && !path.includes('/enter-otp')
      if (shouldFetchBrandData) {
        hasFetchedRef.current = true
        try {
          await useAccessStore.getState().fetchBrandData()
        } catch (_e) {
          logError('Failed to load brand data during guard', { e: _e })
        }
        return
      }

      if (isAuthenticated && bd) {
        const registrationStep = bd.registrationStep

        if (path.startsWith('/registration') && !validRegistrationPaths.includes(path)) {
          const targetRoute = useAccessStore.getState().getRouteByRegistrationStep(registrationStep || 2)
          if (targetRoute) {
            navigate(targetRoute, { replace: true })
            return
          }
        }

        if (registrationStep === 6 && (path === '/login' || path.startsWith('/forgot-password'))) {
          navigate('/campaigns', { replace: true })
          return
        }

        if (path.startsWith('/registration') && validRegistrationPaths.includes(path)) {
          return
        }
      }

      // OLD LOGIC - Commented out
      // // Handle invalid registration paths
      // if (isAuthenticated && path.startsWith('/registration') && !validRegistrationPaths.includes(path)) {
      //   const currentStep = getCurrentRegistrationStep(bd)
      //   if (currentStep) {
      //     navigate(currentStep, { replace: true })
      //     return
      //   }
      //   navigate('/registration/profile', { replace: true })
      //   return
      // }

      // // Check registration step 1: Profile
      // if (isAuthenticated && (!bd?.firstName || !bd?.lastName || !bd?.phoneNumber || !bd?.region)) {
      //   if (path !== '/registration/profile') {
      //     navigate('/registration/profile', { replace: true })
      //   }
      //   return
      // }

      // // Check registration step 2: Industries
      // if (
      //   isAuthenticated &&
      //   bd?.firstName &&
      //   bd?.lastName &&
      //   bd?.phoneNumber &&
      //   bd?.region &&
      //   (!bd?.industries || !Array.isArray(bd.industries) || bd.industries.length === 0)
      // ) {
      //   if (path !== '/registration/industries') {
      //     navigate('/registration/industries', { replace: true })
      //   }
      //   return
      // }

      // // Check registration step 3: Business
      // if (
      //   isAuthenticated &&
      //   bd?.firstName &&
      //   bd?.lastName &&
      //   bd?.phoneNumber &&
      //   bd?.region &&
      //   bd?.industries &&
      //   Array.isArray(bd.industries) &&
      //   bd.industries.length > 0 &&
      //   (!bd?.companyName || !bd?.jobPosition || !bd?.annualBudgetCurrency || !bd?.annualBudget || !bd?.companySize)
      // ) {
      //   if (path !== '/registration/business') {
      //     navigate('/registration/business', { replace: true })
      //   }
      //   return
      // }

      // // Check registration step 4: Logo and completion
      // if (
      //   isAuthenticated &&
      //   bd?.firstName &&
      //   bd?.lastName &&
      //   bd?.phoneNumber &&
      //   bd?.region &&
      //   bd?.industries &&
      //   Array.isArray(bd.industries) &&
      //   bd.industries.length > 0 &&
      //   bd?.companyName &&
      //   bd?.jobPosition &&
      //   bd?.annualBudgetCurrency &&
      //   bd?.annualBudget &&
      //   bd?.companySize
      // ) {
      //   const brandLogoUrl = bd?.brandLogoUrl as string | undefined

      //   // No logo - redirect to upload
      //   if (!brandLogoUrl) {
      //     if (path !== '/registration/upload-logo') {
      //       navigate('/registration/upload-logo', { replace: true })
      //     }
      //     return
      //   }

      //   // Has logo and on upload page - go to success
      //   if (path === '/registration/upload-logo') {
      //     navigate('/registration/account-ready', { replace: true })
      //     return
      //   }

      //   // On success page - stay there
      //   if (path === '/registration/account-ready') {
      //     return
      //   }

      //   // Registration complete - redirect to campaigns if still on auth pages
      //   if (path.startsWith('/registration') || path === '/login' || path.startsWith('/forgot-password')) {
      //     navigate('/campaigns', { replace: true })
      //   }
      //   return
      // }
    }

    void decideRoute()
  }, [isAuthenticated, initialized, navigate, location.pathname, brandData, registrationStatus, checkRegistrationStatus])
}
